import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const DOTS = Array.from({ length: 34 }, (_, index) => ({
  left: (index * 29) % 92,
  top: (index * 47) % 86,
  size: 3 + (index % 4),
}));

function PointCloudViewer({ file }) {
  const { colors } = useTheme();
  const url = file?.url || file?.path || file?.signedUrl || file?.cdnPath;
  const name = file?.originalName || file?.name || 'Point cloud';

  const styles = useMemo(() => StyleSheet.create({
    viewer: {
      width: '100%',
      minHeight: 220,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      padding: 16,
      gap: 10,
    },
    cloud: {
      height: 140,
      borderRadius: 12,
      backgroundColor: colors.solid_color_block,
      position: 'relative',
      overflow: 'hidden',
    },
    dot: {
      position: 'absolute',
      borderRadius: 999,
      backgroundColor: colors.text_color_primary,
      opacity: 0.62,
    },
    footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 8,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
  }), [colors]);

  return (
    <View style={styles.viewer}>
      <View style={styles.cloud}>
        {DOTS.map((dot, index) => (
          <View
            key={index}
            style={[styles.dot, {
              left: `${dot.left}%`,
              top: `${dot.top}%`,
              width: dot.size,
              height: dot.size,
            }]}
          />
        ))}
      </View>
      <View style={styles.footer}>
        <View>
          <Text size='s' color='primary' weight='bold' numberOfLines={1}>{name}</Text>
          <Text size='xs' color='secondary'>Point cloud preview surface</Text>
        </View>
        {!!url && Platform.OS === 'web' && (
          <Pressable style={styles.button} onPress={() => window.open(url, '_blank', 'noopener,noreferrer')}>
            <Icon name='download' size={12} color={colors.text_color_secondary} />
            <Text size='xs' color='secondary'>Open</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

export default React.memo(PointCloudViewer);
